import { SuccessMessageResponse } from 'models/schema-generated-models/api-response-bodies/success-message-response';
import { UserPoolPropertiesResponse, Convert as UserPoolPropertiesResponseConvert } from 'models/schema-generated-models/api-response-bodies/user-pool-properties-response';
import { UserPoolsResponse, Convert as UserPoolsResponseConvert } from 'models/schema-generated-models/api-response-bodies/user-pools-response';
import { UserPoolMappingRequest } from 'models/schema-generated-models/requests/idp/user-pool-mapping-request';
import { SSOAPIContextParser } from 'services/api-context-service/sso-api-context-parser';
import {
    SSOAPINotFoundError,
    SSOAPIUnexpectedResponseBody
} from 'services/errors';
import { BaseService } from 'services/sso-api/base-service';
import { ClientResponse } from '../client/client-response';

export enum UserPoolRoute {
    USER_POOLS = '/idp/user-pools',
    USER_POOL_MAPPING = '/idp/user-pool-mapping',
    POOL_PROPERTIES = '/idp/federated-pool-properties',
    CREATE_ENTERPRISE_MAPPING = '/idp/federated-pool-enterprise-mapping'
}

export class UserPoolService extends BaseService {
    getPoolProperties = async (): Promise<UserPoolPropertiesResponse> => {
        const response: ClientResponse = await this.client.get(UserPoolRoute.POOL_PROPERTIES);
        const body = await response.text();
        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);

        if (response.status === 200) {
            try {
                return UserPoolPropertiesResponseConvert.toUserPoolPropertiesResponse(body);
            } catch (err) {
                throw new SSOAPIUnexpectedResponseBody(responseAPIContext, 'Unexpected response body received.', { cause: err as Error });
            }
        }

        if (response.status === 404) {
            throw new SSOAPINotFoundError(responseAPIContext);
        }

        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    getUserPools = async (): Promise<UserPoolsResponse> => {
        const response: ClientResponse = await this.client.get(UserPoolRoute.USER_POOLS);
        const body = await response.text();
        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);

        if (response.status === 200) {
            try {
                return UserPoolsResponseConvert.toUserPoolsResponse(body);
            } catch (err) {
                throw new SSOAPIUnexpectedResponseBody(responseAPIContext, 'Unexpected response body received.', { cause: err as Error });
            }
        }

        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    createUserPoolMapping = async (): Promise<SuccessMessageResponse> => {
        const response: ClientResponse = await this.client.post(UserPoolRoute.CREATE_ENTERPRISE_MAPPING, {});

        if (response && response.status < 300 && response.status >= 200) {
            const body = await response.json();
            return body;
        }

        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);
        const body = await response.text();
        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };

    putUserPoolMapping = async (userPoolMappingRequest: UserPoolMappingRequest): Promise<UserPoolPropertiesResponse> => {
        const response: ClientResponse = await this.client.put(UserPoolRoute.USER_POOL_MAPPING, { body: JSON.stringify(userPoolMappingRequest) });

        const body = await response.text();
        const responseAPIContext = SSOAPIContextParser.getSSOAPIContextFromResponse(response);

        if (response.status === 200) {
            try {
                return UserPoolPropertiesResponseConvert.toUserPoolPropertiesResponse(body);
            } catch (err) {
                throw new SSOAPIUnexpectedResponseBody(responseAPIContext, 'Unexpected response body received.', { cause: err as Error });
            }
        }

        throw this.processGeneralAPIError(response, body, responseAPIContext);
    };
}
